import { GateMovementMessageType as MessageType, MetricUnit } from '@/__generated__/graphql';
import { Formatters } from '@/hooks/useFormatters';
import { formatPosition } from '@features/gateCommon/utils/formatPosition';
import { GREEN, ORANGE, RED } from '@features/gateOperationModal/constants';
import { I18n } from '@lingui/core';
import { t, Trans } from '@lingui/macro';
import {
  ArrowUpward,
  BackHand,
  Battery4Bar,
  CheckCircleOutline,
  PanTool,
  SvgIconComponent,
  VerifiedUser,
  Wifi,
} from '@mui/icons-material';
import { FC, PropsWithChildren, ReactNode } from 'react';

export interface NotificationData {
  id: number;
  type: MessageType;
  timestamp: number;
  payload?: Record<string, string | number | null> | null;
}

type Attributes = {
  Icon: SvgIconComponent;
  bgcolor: string;
  fgcolor: string;
};

const DefaultAccent: FC<PropsWithChildren> = ({ children }) => children;

type TextFormatters = {
  titleFormatter: (
    payload: NotificationData['payload'],
    Accent?: FC<PropsWithChildren>,
  ) => ReactNode;
  contentFormatter?: (payload: NotificationData['payload']) => ReactNode;
};

export const getAttributes = (type: MessageType) => {
  const successColors = {
    fgcolor: 'white',
    bgcolor: GREEN,
  };

  const errorColors = {
    fgcolor: 'white',
    bgcolor: RED,
  };

  const neutralColors = {
    fgcolor: 'white',
    bgcolor: '#222B36',
  };

  const warningColors = {
    fgcolor: 'white',
    bgcolor: ORANGE,
  };

  const iconMap: Record<MessageType, Attributes> = {
    [MessageType.Initializing]: { Icon: Wifi, ...neutralColors },
    [MessageType.StopOperation]: { Icon: PanTool, ...warningColors },
    [MessageType.Complete]: { Icon: CheckCircleOutline, ...successColors },
    [MessageType.Battery]: { Icon: Battery4Bar, ...neutralColors },
    [MessageType.Movement]: { Icon: ArrowUpward, ...neutralColors },
    [MessageType.ValidateMovement]: { Icon: VerifiedUser, ...neutralColors },
    [MessageType.EmergencyStop]: { Icon: BackHand, ...errorColors },
  };

  return iconMap[type];
};

export const getTextFormatters = (type: MessageType, i18n: I18n, formatters: Formatters) => {
  const formattersMap: Record<MessageType, TextFormatters> = {
    [MessageType.Initializing]: {
      titleFormatter: () => t(i18n)`Iniciando comunicación con la compuerta`,
      contentFormatter: () => t(i18n)`Se está enviando la solicitud de operación a la compuerta`,
    },
    [MessageType.StopOperation]: {
      titleFormatter: () => t(i18n)`Operación incompleta - Se ha detenido el movimiento`,
      contentFormatter: () => t(i18n)`Se ha detenido el movimiento de la compuerta remotamente`,
    },
    [MessageType.Complete]: {
      titleFormatter: () => t(i18n)`Operación completada`,
      contentFormatter: (payload) => {
        let position = '?';
        if (payload?.position && typeof payload.position === 'number') {
          position = formatPosition(payload.position, formatters.formatMetricValue);
        }
        return t(i18n)`Compuerta ha llegado a la posición ${position}`;
      },
    },
    [MessageType.Battery]: {
      titleFormatter: () => t(i18n)`Nivel de batería`,
      contentFormatter: (payload) => {
        let battery = '?';
        if (payload && 'batteryLevel' in payload && typeof payload.batteryLevel === 'number') {
          battery = formatters.formatMetricValue(payload.batteryLevel, {
            unit: MetricUnit.Percent,
            precision: 1,
          });
        }
        return t(i18n)`El nivel de la batería es ${battery}`;
      },
    },
    [MessageType.Movement]: {
      titleFormatter: (payload, Accent = DefaultAccent) => {
        let position = '?';
        if (payload?.position && typeof payload.position === 'number') {
          position = formatPosition(payload.position, formatters.formatMetricValue);
        }
        return (
          <Trans>
            Compuerta en movimiento: - <Accent>posición actual: {position}</Accent>
          </Trans>
        );
      },
    },
    [MessageType.ValidateMovement]: {
      titleFormatter: () => t(i18n)`Compuerta ha validado el movimiento`,
      contentFormatter: () => t(i18n)`Iniciando el movimiento solicitado`,
    },
    [MessageType.EmergencyStop]: {
      titleFormatter: () => t(i18n)`Operación incompleta - Parada de emergencia ACTIVA`,
      contentFormatter: () =>
        t(i18n)`No se podrá operar hasta su deshabilitación en el tablero compuerta`,
    },
  };
  return formattersMap[type];
};
